/* eslint-disable react/prop-types */
import { cn } from '@/utils/cn';
import { CloseOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { get } from 'lodash';
import React, {
  ForwardRefExoticComponent,
  RefAttributes,
  forwardRef,
  memo,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'; // Add missing React import
import { comps } from './modalComps';

export type ModalTypeProps =
  | 'LOGIN'
  | 'REGISTER'
  | 'START_SHIFT'
  | 'DISCOUNTS'
  | 'TICKET_SAVE'
  | 'SEND_EMAIL'
  | 'POS_SETUP'
  | 'FORGET_PASSWORD'
  | 'ADD_CASH_FORM'
  | 'ITEM_DETAILS';

// Updated types for better type safety
interface ModalState {
  type?: ModalTypeProps;
  show?: boolean;
  data?: Record<string, unknown> | null;
}

interface ModalSwitcherProps {
  type?: ModalTypeProps;
  show?: boolean;
  data?: Record<string, unknown> | null;
  containerClassName?: string;
  withCloseIcon?: boolean;
  compProps?: Record<string, unknown>;
  onClose?: () => void;
}

const ModalSwitcher: ForwardRefExoticComponent<ModalSwitcherProps & RefAttributes<unknown>> =
  forwardRef(
    (
      {
        type = '',
        show = false,
        data = null,
        withCloseIcon = true,
        containerClassName = '',
        compProps = {},
        onClose,
      },
      ref
    ) => {
      const [modalState, setModalState] = useState<ModalState | null>(null);

      const { modalType, modalShow, modalData } = useMemo(() => {
        return {
          modalType: modalState?.type ?? type,
          modalShow: modalState?.show ?? show,
          modalData: modalState?.data ?? data ?? {},
        };
      }, [modalState, show, type, data]);

      const handleClose = () => {
        setModalState(null);
        onClose?.();
      };

      // Add validation to ensure modalData and compProps are properly structured
      const openModal = (show: boolean, type?: ModalTypeProps, data?: Record<string, unknown>) => {
        if (!show || !(type || modalType)) {
          setModalState(null);
          onClose?.();
        } else if (show !== modalShow || type !== modalType) {
          // Validate data to ensure no invalid objects are passed
          const validatedData = Object.entries(data || {}).reduce(
            (acc, [key, value]) => {
              // Keep all values except React elements
              if (!React.isValidElement(value)) {
                acc[key] = value;
              }
              return acc;
            },
            {} as Record<string, unknown>
          );

          setModalState({
            type: type as ModalTypeProps,
            show,
            data: { ...modalData, ...validatedData },
          });
        }
      };

      useImperativeHandle(ref, () => ({
        openModal(show = true, type = '', data = {}) {
          openModal(show, type as ModalTypeProps, data as Record<string, unknown>);
        },
      }));

      const Component = useMemo(() => {
        if (modalShow && modalType) {
          return get(comps, modalType, comps.FRAGMENT);
        }
        return () => null;
      }, [modalType, modalShow]);

      return (
        <Modal
          open={modalShow}
          onCancel={handleClose}
          footer={null}
          closeIcon={withCloseIcon ? <CloseOutlined className="text-gray-500" /> : null}
          className={cn('!p-0', containerClassName)}
        >
          <div className={cn('flex-1', containerClassName)}>
            <Component {...compProps} {...(modalData ?? {})} />
          </div>
        </Modal>
      );
    }
  );

ModalSwitcher.displayName = 'ModalSwitcher';

export default memo(ModalSwitcher);
